<script lang="ts">
  import { onMount } from "svelte";

  import * as yup from "yup";
  import { Form } from "$ui/form";
  import { Button } from "$ui/button";
  import { Input } from "$ui/input";

  import BinIcon from "$assets/icons/bin.svelte";

  import { cn } from "$utils/ui";
  import { CircleHelp, Plus } from "lucide-svelte";
  import { authStateStore } from "$lib/stores/user";
  import { DrawerHeader, DrawerTitle } from "$lib/ui/drawer";

  type FormProps = {
    connectionId?: string;
    initialValues?: { emails: string[] } | undefined;
    isEdit?: boolean;
    onSuccess?: () => void;
    form?: any;
    errors?: any;
    touched?: any;
    isSubmitting?: any;
    onSubmit?: ({ emails }: { emails: string[] }) => Promise<void>;
    handleSubmit?: () => void;
    standaloneForm?: boolean;
    validateField?: (field?: any) => any;
    updateValidateField?: () => void;
  };

  let isEdit: boolean = false;
  let connectionId;
  let initialValues: any = {
    emails: [null],
  };

  $: auth = $authStateStore;

  const validationSchema = yup.object().shape({
    emails: yup.array().of(
      yup
        .string()
        .required("Email cannot be empty")
        .email("Email is invalid")
        .test("valid-domain", "You can only invite members who share your email domain", (value) => {
          if (!value) return false;
          const allowedDomains = [auth?.userInfo?.email?.split("@")[1]];
          const domain = value.split("@")[1];
          return allowedDomains.includes(domain);
        })
        .test("duplicated email", "You already input this email", (value) => {
          return $form.emails.reduce((count: number, o: string) => count + (o === value ? 1 : 0), 0) <= 1;
        }),
    ),
  });

  let standaloneForm: FormProps["standaloneForm"] = true;
  let onSuccess: FormProps["onSuccess"] = () => {};

  let form: FormProps["form"] = null;
  let errors: FormProps["errors"] = null;
  let touched: FormProps["touched"] = null;
  let isSubmitting: FormProps["isSubmitting"] = false;
  let handleSubmit: FormProps["handleSubmit"] = () => {};
  let onSubmit: FormProps["onSubmit"] = async ({ emails }) => {};
  let validateField: FormProps["validateField"];
  let updateValidateField: FormProps["updateValidateField"];

  onMount(() => {
    $touched.emails[0] = null;
  });

  const add = () => {
    $form.emails = [...$form.emails, null];
    $errors.emails = [...$errors.emails, ""];
    $touched.emails = [...$touched.emails, null];
  };

  const remove = (index: string | number) => () => {
    $form.emails = $form.emails.filter((_: any, currentIndex: string | number) => currentIndex !== index);
    $errors.emails = $errors.emails.filter((_: any, currentIndex: string | number) => currentIndex !== index);
  };

  export {
    connectionId,
    wizardValues,
    form,
    errors,
    touched,
    isSubmitting,
    handleSubmit,
    standaloneForm,
    onSubmit,
    onSuccess,
    initialValues,
    isEdit,
  };
</script>

<DrawerHeader class="pt-8 px-6 pb-4 relative">
  <DrawerTitle class="text-left">Invite your {connectionId} admin from your organization</DrawerTitle>
</DrawerHeader>

<div class="flex-1 flex flex-col items-stretch px-6">
  <Form
    {initialValues}
    {validationSchema}
    {onSubmit}
    {onSuccess}
    {standaloneForm}
    submittingButtonText="Sending Invites"
    submitButtonText="Send Invites"
    bind:form
    bind:handleSubmit
    bind:errors
    bind:validateField
    bind:updateValidateField
    bind:isSubmitting
    bind:touched
  >
    {#if $form}
      <div class="flex items-center gap-2">
        <span>Invite admin of {connectionId}</span>
        <CircleHelp size="20" />
      </div>
      {#each $form.emails as email, index}
        <div class="flex flex-col">
          <div class={cn("flex flex-col md:flex-row gap-4 [&>div]:flex-1", isEdit && "flex-col")}>
            <Input
              name={`emails[${index}]`}
              placeholder="e.g. user@example.com"
              error={($errors.emails && $errors.emails[index]) || ""}
              readOnly={isEdit}
              bind:value={email}
              on:input={() => {
                validateField && validateField(`emails[${index}]`);
              }}
            />

            {#if !isEdit}
              <div class="!flex-[0_0_24px] mt-2">
                <button
                  type="button"
                  on:click={remove(index)}
                  disabled={$form.emails.length === 1}
                  class={cn("[&>svg]:w-3.5", $form.emails.length === 1 && "opacity-30 cursor-not-allowed")}
                >
                  <BinIcon />
                </button>
              </div>
            {/if}
          </div>
          {#if index === $form.emails.length - 1 && !isEdit}
            <div class="mt-2">
              <Button on:click={add} variant="outline" size="xs">
                <Plus size={16} />
                Add another email
              </Button>
            </div>
          {/if}
        </div>
      {/each}
    {/if}
  </Form>
</div>
